import { WORKFLOW_CANVAS as C, WORKFLOW_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { toast } from "@sagot/ui";
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { exitName } from "../builder/step-summary";
import { connectToNewPath, setExit } from "../definition-model";
import { effectiveFallback, exitSlotsOf, type ExitRef, type ExitSlot } from "../exit-slots";
import type { Definition } from "../types";
import { tidyLayout, type Box, type Point } from "./canvas-layout";
import { connectTarget, type RefusalReason } from "./connector-rules";

const K = "engage.workflows.connectors";

interface Options {
  definition: Definition;
  editable: boolean;
  boxes: Readonly<Record<string, Box>>;
  viewport: RefObject<HTMLDivElement | null>;
  scrollToBox: (box: Box) => void;
  onChange: (definition: Definition) => void;
  /** Puts a tile somewhere other than its tidy spot (a view setting, never saved). */
  place: (id: string, offset: Point) => void;
}

/** What the author does to connectors, as definition changes plus the feedback that goes with them: announcements and toasts. */
export function useConnections({ definition, editable, boxes, viewport, scrollToBox, onChange, place }: Options) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [highlight, setHighlight] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const highlightTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => { window.clearTimeout(highlightTimer.current); }, []);

  const announce = useCallback((text: string) => { setAnnouncement(text); }, []);
  const pathTitle = (id: string): string => {
    const label = definition.paths[id]?.label;
    return `${t("engage.workflows.builder.path", { id })}${label ? ` · ${label}` : ""}`;
  };
  /** "D · Tour": the short form of a path's name, for the chip on an exit row. */
  const pathChip = (id: string): string => {
    const label = definition.paths[id]?.label;
    return label ? `${id} · ${label}` : id;
  };
  const slotOf = (ref: ExitRef): ExitSlot | undefined => {
    const step = definition.paths[ref.path]?.steps[ref.index];
    return step ? exitSlotsOf(step).find((slot) => slot.key === ref.key) : undefined;
  };
  const nameOf = (ref: ExitRef): string => {
    const slot = slotOf(ref);
    return slot ? exitName(slot, t) : ref.key;
  };
  const refuse = (reason: RefusalReason) => { toast.warning(t(`${K}.refused.${reason}`, { max: WORKFLOW_LIMITS.paths })); };

  const connect = (ref: ExitRef, target: string) => {
    onChange(setExit(definition, ref, ref.key, target));
    announce(t(`${K}.announce.connected`, { exit: nameOf(ref), path: pathTitle(target) }));
  };
  const disconnect = (ref: ExitRef) => {
    const slot = slotOf(ref);
    if (!slot?.target) return;
    onChange(setExit(definition, ref, ref.key, undefined));
    const lastStep = ref.index === (definition.paths[ref.path]?.steps.length ?? 0) - 1;
    announce(t(`${K}.announce.deleted`, { exit: nameOf(ref), after: t(`${K}.after.${effectiveFallback(slot, lastStep)}`) }));
  };
  /** A new path for the exit; dropped on the canvas it appears at `point`, from the menu at its tidy spot. */
  const createPath = (ref: ExitRef, point?: Point) => {
    const created = connectToNewPath(definition, ref, ref.key);
    if (!created) {
      refuse("full");
      return;
    }
    onChange(created.definition);
    const tidy = tidyLayout(created.definition, editable)[created.pathId];
    if (point && tidy) place(created.pathId, { x: point.x + C.dropNewPathOffset - tidy.x, y: point.y - C.headerHeight / 2 - tidy.y });
    announce(t(`${K}.announce.created`, { id: created.pathId, exit: nameOf(ref) }));
  };
  const goTo = (tileId: string) => {
    const box = boxes[tileId];
    if (!box) return;
    scrollToBox(box);
    setHighlight(tileId);
    window.clearTimeout(highlightTimer.current);
    highlightTimer.current = window.setTimeout(() => { setHighlight((current) => (current === tileId ? null : current)); }, C.highlightMs);
    window.requestAnimationFrame(() => {
      viewport.current?.querySelector<HTMLElement>(`[data-tile-heading="${tileId}"]`)?.focus({ preventScroll: true });
    });
    announce(t(`${K}.announce.wentTo`, { path: pathTitle(tileId) }));
  };

  /** A connector was dropped on a tile, or on empty canvas (`over` is `null`). */
  const drop = (ref: ExitRef, over: string | null, point: Point) => {
    if (over !== null && slotOf(ref)?.target === over) return;
    const check = connectTarget(definition, ref, over);
    if (!check.ok) {
      refuse(check.reason);
      announce(t(`${K}.refused.${check.reason}`, { max: WORKFLOW_LIMITS.paths }));
      return;
    }
    if (over === null) createPath(ref, point);
    else connect(ref, over);
  };

  return {
    selected,
    menuFor,
    highlight,
    announcement,
    select: setSelected,
    focused,
    focus: setFocused,
    openMenu: setMenuFor,
    pathTitle,
    pathChip,
    nameOf,
    connect,
    disconnect,
    createPath,
    goTo,
    drop,
    onDragStart: (ref: ExitRef) => { announce(t(`${K}.announce.dragging`, { exit: nameOf(ref) })); },
    onDragCancel: () => { announce(t(`${K}.announce.cancelled`)); },
  };
}
